
import { useCallback, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { permissionsCircuitBreaker } from '@/utils/circuitBreaker';
import type { PermissionsState } from '@/types/permissions';
import { paymentRoutingService } from '@/services/payments';
import { invalidateUserPermissionsCache } from '@/services/permissions/cache/cacheService';

/**
 * Hook for permissions API operations
 */
export const usePermissionsAPI = (session: Session | null, refreshPermissions?: () => Promise<void>) => {
  const fetchFreshPermissions = useCallback(async (userId: string): Promise<PermissionsState> => {
    console.log('🔍 Permissions API: Fetching fresh permissions for user:', userId);

    const result = await permissionsCircuitBreaker.execute(async () => {
      console.log('📞 Permissions API: Calling check-permissions Edge function');

      try {
        const { data, error } = await supabase.functions.invoke('check-permissions', {
          headers: {
            Authorization: `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({ userId })
        });

        console.log('📋 Permissions API: Edge function response:', { data, error });

        if (error) {
          console.error('❌ Permissions API: Edge function error:', error);
          throw error;
        }

        if (!data) {
          console.error('❌ Permissions API: No data returned from Edge function');
          throw new Error('No data returned from permissions API');
        }

        console.log('✅ Permissions API: Successfully received permissions data:', {
          isPremium: data.isPremium,
          isAdmin: data.isAdmin,
          source: data.source,
          timestamp: data.timestamp
        });

        return data;
      } catch (apiError) {
        console.error('❌ Permissions API: Exception during API call:', apiError);
        throw apiError;
      }
    });

    return result;
  }, [session?.access_token]);

  // Set user ID for iOS IAP database integration when session changes
  useEffect(() => {
    if (session?.user?.id) {
      console.log('👤 Permissions API: Setting user ID for payment routing service');
      paymentRoutingService.setCurrentUserId(session.user.id);
    }
  }, [session?.user?.id]);

  // Set up subscription sync callback with debouncing to prevent infinite loops
  useEffect(() => {
    if (!session?.user?.id || !refreshPermissions) {
      return;
    }

    console.log('🔗 Permissions API: Setting up subscription sync callback with debouncing');

    let debounceTimeout: NodeJS.Timeout | null = null;

    const debouncedRefresh = async () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      debounceTimeout = setTimeout(async () => {
        console.log('🔄 Permissions API: Subscription sync triggered - refreshing permissions');

        // CRITICAL: Invalidate cache before refreshing to ensure fresh data
        if (session?.user?.id) {
          console.log('🗑️ Permissions API: Invalidating cache due to subscription change');
          invalidateUserPermissionsCache(
            session.user.id,
            'subscription_change_ios_iap',
            Date.now()
          );
        }

        try {
          await refreshPermissions();
          console.log('✅ Permissions API: Subscription sync refresh completed');
        } catch (error) {
          console.error('❌ Permissions API: Subscription sync refresh failed:', error);

          // If permissions refresh fails due to auth issues, try refreshing the session first
          console.log('🔄 Permissions API: Attempting session refresh and retry...');
          try {
            const { error: sessionError } = await supabase.auth.refreshSession();
            if (sessionError) {
              console.error('❌ Permissions API: Session refresh failed:', sessionError);
            } else {
              console.log('✅ Permissions API: Session refreshed, retrying permissions refresh...');
              await refreshPermissions();
              console.log('✅ Permissions API: Retry successful after session refresh');
            }
          } catch (retryError) {
            console.error('❌ Permissions API: Retry after session refresh failed:', retryError);
            console.log('💡 Permissions API: User may need to refresh the app to see subscription changes');
          }
        }
      }, 1000); // 1 second debounce
    };

    paymentRoutingService.setSubscriptionSyncCallback(debouncedRefresh);

    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [session?.user?.id, refreshPermissions]);

  const forceRefreshPermissions = useCallback(async (): Promise<void> => {
    if (!session?.user?.id) {
      console.error('❌ Permissions API: Cannot force refresh - user not authenticated');
      throw new Error('User not authenticated');
    }

    console.log('🔄 Permissions API: Force refreshing permissions (manual trigger)');

    try {
      // Clear any cached permissions to force fresh fetch
      if (session.user.id) {
        console.log('🗑️ Permissions API: Clearing cache for force refresh');
        localStorage.setItem(`permissions_invalidated_${session.user.id}`, Date.now().toString());
        localStorage.removeItem(`permissions_cache_${session.user.id}`);
        localStorage.removeItem(`permissions_last_refreshed_${session.user.id}`);
      }

      // Force call the refresh function
      console.log('🔍 Permissions API: Force refresh - refreshPermissions function available:', !!refreshPermissions);
      if (refreshPermissions) {
        console.log('🔄 Permissions API: About to call refreshPermissions for force refresh...');
        await refreshPermissions();
        console.log('✅ Permissions API: Force refresh completed successfully');
      } else {
        console.error('❌ Permissions API: refreshPermissions function not available for force refresh');
        throw new Error('Refresh function not available');
      }
    } catch (error) {
      console.error('❌ Permissions API: Force refresh failed:', error);
      throw error;
    }
  }, [session?.user?.id, refreshPermissions]);

  const initiateSubscription = useCallback(async (
    priceType: 'monthly' | 'annual' = 'monthly',
    referralCode?: string
  ): Promise<void> => {
    if (!session?.access_token) {
      throw new Error('User not authenticated');
    }

    console.log('🔄 Permissions API: Routing payment through payment routing service');

    // Use the payment routing service to handle platform-specific payment logic
    const result = await paymentRoutingService.initiatePayment({
      priceType,
      referralCode
    });

    if (!result.success) {
      console.error('❌ Permissions API: Payment routing failed:', result.error);
      throw new Error(result.error || 'Payment initiation failed');
    }

    console.log(`✅ Permissions API: Payment initiated via ${result.method}`);
  }, [session]);

  return {
    fetchFreshPermissions,
    initiateSubscription,
    forceRefreshPermissions,
  };
};
